/** Peu públic d'una foto. Una distància només surt si la característica és pública i té valor. */
export function publicCaption(m, p, catalog) {
    const c = m.caption;
    if (!c)
        return undefined;
    if (c.kind === 'text')
        return Object.keys(c.text).length > 0 ? { kind: 'text', text: c.text } : undefined;
    if (c.kind === 'priceFrom')
        return { kind: 'priceFrom' };
    const spec = p.specs?.[c.specKey];
    const item = catalog.items.find((i) => i.key === c.specKey && i.type === 'distance');
    if (!spec || !item || spec.visibleTo !== 'public')
        return undefined;
    const d = spec.value;
    return { kind: 'distance', label: item.label, ...(d.km !== undefined ? { km: d.km } : {}), ...(d.minutes !== undefined ? { minutes: d.minutes } : {}) };
}
const photoOf = (m, caption) => ({
    id: m.id,
    ...(caption ? { caption } : {}),
    variants: m.variants,
    width: m.width,
    height: m.height,
    alt: m.alt ?? {},
    room: m.room ?? null,
});
/** Desplaçament fix per a cada casa (hash de l'id): el punt aproximat no canvia d'un dia a l'altre. */
export const APPROX_RADIUS_M = 400;
export function publicPoint(propertyId, geo, exact) {
    if (!geo)
        return undefined;
    if (exact)
        return { lat: geo.lat, lng: geo.lng, exact: true, radiusM: 0 };
    let h = 2166136261;
    for (const ch of propertyId)
        h = Math.imul(h ^ ch.charCodeAt(0), 16777619) >>> 0;
    const angle = ((h % 3600) / 3600) * 2 * Math.PI;
    const dist = 100 + ((h >>> 12) % 151); // de 100 a 250 m
    const dLat = (dist * Math.cos(angle)) / 111_320;
    const dLng = (dist * Math.sin(angle)) / (111_320 * Math.cos((geo.lat * Math.PI) / 180));
    const round = (x) => Math.round(x * 1e5) / 1e5;
    return { lat: round(geo.lat + dLat), lng: round(geo.lng + dLng), exact: false, radiusM: APPROX_RADIUS_M };
}
/** Característiques visibles per a tothom, en l'ordre del catàleg. Les claus desconegudes s'ometen. */
export function publicSpecs(specs, catalog) {
    const groups = [];
    for (const group of catalog.groups) {
        const items = [];
        for (const item of catalog.items) {
            if (item.group !== group.key)
                continue;
            const stored = specs?.[item.key];
            if (!stored || stored.visibleTo !== 'public')
                continue;
            const out = { key: item.key, type: item.type, label: item.label, value: stored.value };
            if (item.unit)
                out.unit = item.unit;
            if (stored.note && Object.keys(stored.note).length > 0)
                out.note = stored.note;
            if (item.options) {
                const chosen = new Set(Array.isArray(stored.value) ? stored.value.map(String) : [String(stored.value)]);
                const options = Object.fromEntries(item.options.filter((o) => chosen.has(o.key)).map((o) => [o.key, o.label]));
                if (Object.keys(options).length > 0)
                    out.options = options;
            }
            items.push(out);
        }
        if (items.length > 0)
            groups.push({ key: group.key, label: group.label, items });
    }
    return groups;
}
/** Vista pública d'una propietat. `media` en qualsevol ordre; `publishedAt` en mil·lisegons. */
export function buildPublicProperty(propertyId, p, media, catalog, publishedAt, priceFrom) {
    const sorted = [...media].sort((a, b) => a.order - b.order);
    const cover = sorted.find((m) => m.isCover) ?? sorted[0];
    const point = publicPoint(propertyId, p.location?.geo, p.location?.showExact ?? false);
    const withCaption = (m) => photoOf(m, publicCaption(m, p, catalog));
    return {
        propertyId,
        slug: p.slug,
        sourceLocale: (p.sourceLocale ?? 'ca'),
        modes: [...p.modes],
        title: p.title ?? {},
        summary: p.summary ?? {},
        description: p.description ?? {},
        location: { municipality: p.location?.municipality ?? '', island: p.location?.island ?? '', ...(point ? { point } : {}) },
        ...(priceFrom && (priceFrom.nightCents !== undefined || priceFrom.monthCents !== undefined) ? { priceFrom } : {}),
        cover: cover ? { ...withCaption(cover), ...(cover.og ? { og: cover.og } : {}) } : null,
        photos: sorted.filter((m) => m.showInSlider).map(withCaption),
        specs: publicSpecs(p.specs, catalog),
        ...(p.allowInquiryHold ? { allowHold: true } : {}),
        publishedAt,
    };
}
/** Targeta de la home a partir de la vista. Les fotos de la home, amb la portada primer. */
export function buildIndexCard(view, media, p, catalog) {
    const home = [...media].filter((m) => m.showInHome).sort((a, b) => Number(b.isCover) - Number(a.isCover) || a.order - b.order);
    const captionOf = (m) => (p && catalog ? publicCaption(m, p, catalog) : undefined);
    return {
        propertyId: view.propertyId,
        slug: view.slug,
        sourceLocale: view.sourceLocale,
        modes: view.modes,
        title: view.title,
        summary: view.summary,
        location: view.location,
        ...(view.priceFrom ? { priceFrom: view.priceFrom } : {}),
        cover: view.cover,
        homePhotos: home.map((m) => photoOf(m, captionOf(m))),
        publishedAt: view.publishedAt,
    };
}
/** Substitueix (o treu, amb `card` null) la targeta d'una propietat. Ordre: primer les publicades abans. */
export function upsertIndexCard(cards, propertyId, card) {
    const rest = cards.filter((c) => c.propertyId !== propertyId);
    if (card)
        rest.push(card);
    return rest.sort((a, b) => a.publishedAt - b.publishedAt || a.slug.localeCompare(b.slug));
}
/** Quantes targetes hi ha de cada tipus d'operació (filtre de la home i d'Explora, §10.1). */
export function countByMode(cards) {
    const out = { shortRent: 0, longRent: 0, sale: 0 };
    for (const c of cards)
        for (const m of c.modes)
            out[m] += 1;
    return out;
}
